<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed } from 'vue';
import { useBreakpoint } from '@/composables/useBreakpoint';

import GStepperProgress from '@/components/GStepper/components/GStepperProgress.vue';

import type {
  GStepperItemInstance,
  GStepperArrowLeftProperties,
  GStepProgressProperties,
} from '../type/stepper';

const props = defineProps({
  progressPercentage: {
    type: Number,
    default: 0,
  },
  hideRightHeader: Boolean,
  hideLeftHeader: Boolean,
  hideStepCounter: Boolean,
  hideStepperProgress: Boolean,
  showCenterLogo: Boolean,
  backArrowFunction: {
    type: Function,
    default: () => {},
  },
  backArrowProperties: {
    type: Object as PropType<GStepperArrowLeftProperties>,
    default: () => ({ hide: false, disabled: false }),
  },
  stepProgressProperties: {
    type: Object as PropType<GStepProgressProperties>,
  },
  itemsWithDot: {
    type: Array as PropType<GStepperItemInstance[]>,
    default: () => [],
  },
  desktopHeightProgress: {
    type: [Number, String],
    default: 8,
  },
  mobileHeightProgress: {
    type: [Number, String],
    default: 0,
  },
});

const { isMobile, isDesktop } = useBreakpoint();

const emit = defineEmits(['click-left-header', 'click-right-header']);

function onLeftHeader() {
  emit('click-left-header');
}

function onRightHeader() {
  emit('click-right-header');
}

const currentHeightProgress = computed(() => {
  return isMobile.value
    ? props.mobileHeightProgress
    : props.desktopHeightProgress;
});
</script>

<template>
  <div class="bg-sec-def-bg">
    <div
      class="flex items-center"
      :class="{
        'h-[90px] max-w-5xl mx-auto px-lg': isDesktop,
        'justify-between px-lg py-md text-icon-secondary': isMobile,
      }"
    >
      <section
        v-if="!hideLeftHeader"
        :class="{
          'flex justify-center items-center h-full pr-lg': isDesktop,
          'top-btn-left': isMobile,
        }"
      >
        <div class="flex justify-center items-center h-full">
          <slot name="left-mobile-header">
            <img
              v-if="isMobile && backArrowProperties.hide && !showCenterLogo"
              src="@/assets/img/g66-logo-new.svg"
              width="30"
              alt="Logo de Global66"
              @click="onLeftHeader"
            />
            <img
              v-else-if="isDesktop && !showCenterLogo"
              src="@/assets/img/g66-logo-new.svg"
              width="40"
              alt="Logo de Global66"
              @click="onLeftHeader"
            />
            <div
              v-if="!backArrowProperties.hide"
              class="header-btn"
              :class="{
                'cursor-not-allowed bg-primary-def-disabled':
                  backArrowProperties.disabled,
                'ml-lg': isDesktop,
                'ml-none': isMobile,
              }"
              @click="backArrowFunction()"
            >
              <fa-icon :icon="['fas', 'arrow-left']" />
            </div>
          </slot>
        </div>
      </section>

      <section
        class="flex items-center"
        :class="{
          'flex-1 relative': isDesktop,
          'flex-1 px-md': isMobile,
        }"
      >
        <div
          class="flex justify-center w-full z-0"
          :class="{ absolute: isDesktop }"
          v-if="showCenterLogo"
        >
          <img
            :width="isMobile ? '150' : undefined"
            src="@/assets/img/logo-horizontal-icon.svg"
            alt="Logo de Global66"
          />
        </div>

        <div v-else class="flex flex-col relative flex-1">
          <section
            v-if="isMobile && !hideStepCounter"
            class="flex items-center mb-xxs text-primary-txt font-semibold text-3"
          >
            Paso {{ stepProgressProperties?.index }} de
            {{ itemsWithDot.length }}
          </section>

          <g-stepper-progress
            v-if="!hideStepperProgress"
            :height="currentHeightProgress"
            :progress-percentage="progressPercentage"
          />
        </div>
      </section>

      <section
        v-if="!hideRightHeader"
        :class="{
          'flex justify-center items-center h-full ml-lg': isDesktop,
          'top-btn-right': isMobile,
        }"
      >
        <slot name="right-header">
          <div
            class="header-btn"
            :class="{ '-mr-sm': isMobile }"
            data-test="button:close-stepper"
            @click="onRightHeader"
          >
            <fa-icon :icon="['far', 'times']" size="lg" />
          </div>
        </slot>
        <slot v-if="isDesktop" name="right-header-custom-space" />
      </section>

      <div v-if="isMobile">
        <slot name="right-header-custom-space"></slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.step-title {
  @apply w-full text-primary-txt font-semibold flex justify-between mb-md mt-xxs;
}

.header-btn {
  @apply transition duration-300 ease-in-out
        flex justify-center items-center
        text-icon-secondary
        text-6 rounded-full w-10 h-10
        cursor-pointer;

  // El hover solo gana sobre un botón habilitado: cuando está deshabilitado, el
  // template agrega `cursor-not-allowed` y pintarlo de `primary-def` lo haría
  // parecer usable.
  &:not(.cursor-not-allowed) {
    @apply hover:text-primary-def hover:bg-sec-hover-bg;
  }
}

.header-stepper {
  .top-btn-left {
    @apply text-6;
  }

  .top-btn-right {
    @apply text-4;
    @apply flex items-center font-medium;
  }
}
</style>
